import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Image as ImageIcon, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from '@rehub/i18n';
import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE_MB } from '../constants';

interface UploadZoneProps {
  onImageSelected: (file: File) => void;
}

const UploadZone: React.FC<UploadZoneProps> = ({ onImageSelected }) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Show feedback the moment a valid file is picked. The happy path unmounts
  // this component when the app moves to the editor; the safety timer just
  // clears the spinner if we stay mounted (e.g. a login prompt is shown).
  const beginUpload = useCallback((file: File) => {
    setError(null);
    setIsUploading(true);
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    safetyTimerRef.current = setTimeout(() => setIsUploading(false), 15000);
    onImageSelected(file);
  }, [onImageSelected]);

  useEffect(() => () => {
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
  }, []);

  const validateFile = (file: File): boolean => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError(t('photoclear.errorFileType'));
      return false;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(t('photoclear.errorFileSize', { mb: MAX_FILE_SIZE_MB }));
      return false;
    }
    return true;
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (isUploading) return;
    setIsDragging(true);
  }, [isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);
    if (isUploading) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        beginUpload(file);
      }
    }
  }, [isUploading, beginUpload]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        beginUpload(file);
      }
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full w-full p-4 sm:p-6 animate-fade-in">
      <div className="max-w-xl w-full text-center space-y-4 sm:space-y-8">
        <div className="space-y-1.5 sm:space-y-2">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-100">{t('photoclear.uploadHeading')}</h2>
          <p className="text-slate-400 text-sm sm:text-base px-2">
            {t('photoclear.uploadSubtitle')}
          </p>
        </div>

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-3xl sm:rounded-[2rem] p-8 sm:p-12 transition-all duration-300 ease-in-out
            flex flex-col items-center justify-center gap-3 sm:gap-4 group
            ${isUploading
              ? 'border-orange-500/70 bg-orange-900/10 cursor-default'
              : isDragging
                ? 'border-orange-500 bg-orange-900/20 scale-[1.02] shadow-xl shadow-orange-900/10 cursor-pointer'
                : 'border-slate-700 bg-slate-900 hover:border-slate-600 hover:shadow-lg hover:shadow-slate-900/50 cursor-pointer'
            }
          `}
        >
          <input
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            disabled={isUploading}
            className={`absolute inset-0 w-full h-full opacity-0 z-20 ${isUploading ? 'pointer-events-none cursor-default' : 'cursor-pointer'}`}
            onChange={handleFileInput}
          />

          {isUploading ? (
            <>
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center bg-orange-900/40 text-orange-400">
                <Loader2 size={32} className="sm:w-10 sm:h-10 animate-spin" />
              </div>

              <div className="space-y-1 w-full">
                <p className="font-medium text-base sm:text-lg text-slate-100">
                  {t('photoclear.uploadingImage')}
                </p>
                <p className="text-xs sm:text-sm text-slate-400">
                  {t('photoclear.uploadingHint')}
                </p>
                <div className="mt-3 sm:mt-4 h-1.5 w-full max-w-xs mx-auto overflow-hidden rounded-full bg-slate-800">
                  <div className="h-full w-1/3 rounded-full bg-orange-500 animate-upload-progress" />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={`
                w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center transition-colors
                ${isDragging ? 'bg-orange-900/40 text-orange-400' : 'bg-slate-800 text-slate-500 group-hover:bg-slate-700 group-hover:text-orange-400'}
              `}>
                {isDragging ? <Upload size={32} className="sm:w-10 sm:h-10" /> : <ImageIcon size={32} className="sm:w-10 sm:h-10" />}
              </div>

              <div className="space-y-1">
                <p className="font-medium text-base sm:text-lg text-slate-200">
                  {isDragging ? t('photoclear.dropImage') : t('photoclear.clickOrDrag')}
                </p>
                <p className="text-xs sm:text-sm text-slate-500">
                  {t('photoclear.uploadFileHint', { mb: MAX_FILE_SIZE_MB })}
                </p>
              </div>
            </>
          )}
        </div>

        {error && (
          <div className="flex items-center justify-center gap-2 text-red-400 bg-red-900/20 border border-red-900/50 p-3 rounded-lg text-sm font-medium animate-shake">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadZone;